import get from 'lodash/get';
import map from 'lodash/map';
import toLower from 'lodash/toLower';
import type { ActionTree } from 'vuex';

import { COUNTRIES_ISO_CODES, mapArray } from '@emobg/web-utils';

import { getBrand } from '@/utils/get-brand.util';
import { ApiService } from '@api/api.service';
import { LOCALES } from '@consts/locales.const';

import { locales } from '../../locales/index';
import { AppMutations } from '../App/mutations';
import { BrokerGetters } from '../Broker/getters';
import type { State } from '../interfaces/state';
import type { ActionAugments } from '../types/actions';
import { AppGetters } from './getters';
import type { BrokerData, Locale, Translation } from './interfaces';

const api = new ApiService();

export enum AppActions {
  GetIntialLocale = 'App:GET_INITIAL_DATA',
  SetBrokerData = 'App:SET_BROKER_DATA',
  ToggleLoading = 'App:TOGGLE_LOADING',
  GetTranslation = 'App:GET_TRANSLATION',
  GetCountries = 'App:GET_COUNTRIES',
  SetCheckpoint = 'App:SET_CHECKPOINT',
  GetProvinces = 'App:GET_PROVINCES',
  GetCities = 'App:GET_CITIES',
  GetBrand = 'App:GET_BRAND',
}

export interface AppActionsData {
  [AppActions.GetIntialLocale]: (context: ActionAugments) => void;
  [AppActions.ToggleLoading]: (context: ActionAugments) => void;
  [AppActions.SetBrokerData]: (context: ActionAugments, brokerData: BrokerData) => void;
  [AppActions.GetTranslation]: (context: ActionAugments, locale: string) => void;
  [AppActions.GetCountries]: (context: ActionAugments, locale: string) => Promise<void>;
  [AppActions.SetCheckpoint]: (context: ActionAugments, checkpoint: Record<string, string>) => void;
  [AppActions.GetProvinces]: (context: ActionAugments, selectedCountryCode: string) => Promise<void>;
  [AppActions.GetCities]: (context: ActionAugments, payload: { selectedCountryCode: string; search: string }) => Promise<void>;
  [AppActions.GetBrand]: (context: ActionAugments) => void;
}

export const actions: ActionTree<State, AppActionsData> = {
  [AppActions.GetIntialLocale]({ dispatch, getters }) {
    const locale = getters[BrokerGetters.GetLocale];
    dispatch(AppActions.GetTranslation, locale);
  },

  [AppActions.SetBrokerData]({ commit }, brokerData) {
    commit(AppMutations.SetBrokerData, brokerData);
  },

  [AppActions.ToggleLoading]({ commit }) {
    commit(AppMutations.ToggleLoading);
  },

  [AppActions.GetTranslation]({ commit }, locale = LOCALES.en) {
    const translations: Record<Locale, Translation> = locales;
    commit(AppMutations.SetTranslation, get(translations, toLower(locale)));
  },

  async [AppActions.GetCountries]({ commit }, locale) {
    const result = await api.getCountries(locale);
    commit(AppMutations.SetCountries, result.countries);
  },

  async [AppActions.GetCities]({ commit }, { selectedCountryCode = COUNTRIES_ISO_CODES.italy, search = '' }) {
    const result = await api.getCities(selectedCountryCode, search);
    const mappedCities = mapArray(result.cities, (city: string) => ({ label: city, value: city }));
    commit(AppMutations.SetCities, mappedCities);
  },

  [AppActions.SetCheckpoint]({ getters }, checkpoint) {
    const { brokerId, token } = getters[AppGetters.GetBrokerData];
    api.postCheckpoint(brokerId, token, checkpoint);
  },
  async [AppActions.GetProvinces]({ commit }, selectedCountryCode) {
    const { initialData } = (await api.getProvinces(selectedCountryCode)) || {};
    const { provinces } = initialData || {};
    const mappedProvinces = map(provinces, ({ name }) => ({ label: name.trim(), value: name.trim() }));
    commit(AppMutations.SetProvinces, mappedProvinces);
  },
  [AppActions.GetBrand]({ commit }) {
    const brand = getBrand();
    commit(AppMutations.SetBrand, brand);
  },
} as const;
